import { Helmet } from "react-helmet-async";
import { lazy, Suspense, useState, useEffect } from "react";
import HeroSection from "@/components/HeroSection";
import SkipToContent from "@/components/SkipToContent";
import LazySection from "@/components/LazySection";
import { lazyRetry } from "@/lib/lazyRetry";

const Navbar = lazy(() => lazyRetry(() => import("@/components/Navbar")));
const ScrollProgressBar = lazy(() => lazyRetry(() => import("@/components/ScrollProgressBar")));
const BackToTop = lazy(() => lazyRetry(() => import("@/components/BackToTop")));

const FloatingOrbs = lazy(() => lazyRetry(() => import("@/components/FloatingOrbs")));
const Floating3DShapes = lazy(() => lazyRetry(() => import("@/components/Floating3DShapes")));
const AnimatedDivider = lazy(() => lazyRetry(() => import("@/components/AnimatedDivider")));

// Lazy load below-the-fold sections
const SectionTransition = lazy(() => lazyRetry(() => import("@/components/SectionTransition")));
const ProjectXFeature = lazy(() => lazyRetry(() => import("@/components/ProjectXFeature")));
const AboutSection = lazy(() => lazyRetry(() => import("@/components/AboutSection")));
const ProjectsSection = lazy(() => lazyRetry(() => import("@/components/ProjectsSection")));
const ServicesSection = lazy(() => lazyRetry(() => import("@/components/ServicesSection")));
const DiscordPortfolio = lazy(() => lazyRetry(() => import("@/components/DiscordPortfolio")));
const TeamSection = lazy(() => lazyRetry(() => import("@/components/TeamSection")));
const CTASection = lazy(() => lazyRetry(() => import("@/components/CTASection")));
const ContactSection = lazy(() => lazyRetry(() => import("@/components/ContactSection")));
const TestimonialsSection = lazy(() => lazyRetry(() => import("@/components/TestimonialsSection")));
const FAQSection = lazy(() => lazyRetry(() => import("@/components/FAQSection")));
const Footer = lazy(() => lazyRetry(() => import("@/components/Footer")));

// Defer FloatingOrbs until after TTI to reduce main thread blocking
function useIdleDefer(delay = 2000): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = (window as any).requestIdleCallback(() => setReady(true), { timeout: delay });
      return () => (window as any).cancelIdleCallback(id);
    } else {
      const t = setTimeout(() => setReady(true), delay);
      return () => clearTimeout(t);
    }
  }, [delay]);
  return ready;
}

const Index = () => {
  const orbsReady = useIdleDefer(2000);

  return (
    <>
      <Helmet>
        <title>FearlessXCR7 – Next-Gen Digital Solutions Agency</title>
        <meta
          name="description"
          content="FearlessXCR7 is a service agency delivering execution-driven digital solutions for communities, professionals, and businesses. Discord server setup, management & security."
        />
        <meta
          name="keywords"
          content="FearlessXCR7, Discord server setup, digital solutions, community management, Project X, trading community, startup"
        />
        <meta property="og:title" content="FearlessXCR7 – Making the Impossible Possible" />
        <meta
          property="og:description"
          content="Building scalable digital systems for communities & businesses. Execution-driven solutions designed for long-term growth."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://fearlessxcr7.com" />
      </Helmet>

      {/* Skip to Content - Accessibility */}
      <SkipToContent />

      <Suspense fallback={null}>
        <ScrollProgressBar />
      </Suspense>

      {/* Global Floating Orbs Background - deferred until after TTI */}
      {orbsReady && (
        <Suspense fallback={null}>
          <FloatingOrbs />
          <Floating3DShapes />
        </Suspense>
      )}

      <main id="main-content" tabIndex={-1} className="relative z-10 outline-none">
        <Suspense fallback={null}>
          <Navbar />
        </Suspense>
        
        <HeroSection />

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1} direction="left">
            <ProjectXFeature />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <AboutSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1}>
            <ProjectsSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1} direction="left">
            <ServicesSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <DiscordPortfolio />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1}>
            <TeamSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>

        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <TestimonialsSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1} direction="left">
            <CTASection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>

        <LazySection>
          <SectionTransition delay={0.1}>
            <FAQSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>
        
        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <ContactSection />
          </SectionTransition>
        </LazySection>
        
        <LazySection>
          <Footer />
        </LazySection>
        <Suspense fallback={null}>
          <BackToTop />
        </Suspense>
      </main>
    </>
  );
};

export default Index;
